/**
 * A* pathfinder on the tile grid.
 * Ground enemies use this to find optimal routes to the base,
 * allowing them to spread across walkable tiles and recover after being pushed off-path.
 *
 * Tile costs:
 *   PATH/SPAWN/BASE/GRILLE = 1 (preferred)
 *   MUD = 3 (slow terrain penalty)
 *   GROUND/PLACEMENT = 8 (usable but strongly discouraged — enemy won't choose this unless pushed there)
 *   WALL/WATER = impassable
 */

import { MAP_GRID, BASE_POSITIONS, getDecorAt } from '@/map/MapData';
import { TileType, TILE_SIZE, DECOR_INTERACTIONS, DecorInteraction } from '@/config/GameConfig';

// ── Tile traversal costs ──
const TILE_COST: Partial<Record<TileType, number>> = {
  [TileType.PATH]:      1,
  [TileType.SPAWN]:     1,
  [TileType.BASE]:      1,
  [TileType.GRILLE]:    1,
  [TileType.MUD]:       3,
  [TileType.GROUND]:    8,
  [TileType.PLACEMENT]: 8,
  [TileType.BROKEN_PLACEMENT]: 10,
  // WALL and WATER: not in map → impassable
};

// 4-directional neighbors (no diagonals — cleaner movement on grid)
const DIRS = [
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 },
];

interface AStarNode {
  col: number;
  row: number;
  g: number;
  f: number;
  parentKey: string | null;
}

function nodeKey(col: number, row: number): string {
  return `${col},${row}`;
}

/** Convert pixel position to grid tile */
function pixelToGrid(px: number, py: number): { col: number; row: number } {
  return {
    col: Math.floor(px / TILE_SIZE),
    row: Math.floor(py / TILE_SIZE),
  };
}

/** Convert grid tile to pixel center */
function gridToPixel(col: number, row: number): { x: number; y: number } {
  return {
    x: col * TILE_SIZE + TILE_SIZE / 2,
    y: row * TILE_SIZE + TILE_SIZE / 2,
  };
}

/**
 * Heuristic: Manhattan distance to the nearest BASE tile.
 * Admissible for 4-directional movement with min cost = 1.
 */
function heuristic(col: number, row: number, baseTargets: { col: number; row: number }[]): number {
  let best = Infinity;
  for (const b of baseTargets) {
    const d = Math.abs(col - b.col) + Math.abs(row - b.row);
    if (d < best) best = d;
  }
  return best;
}

/**
 * Find path from a pixel position to the nearest BASE tile.
 * Returns array of pixel waypoints (tile centers), or null if no path found.
 * The first waypoint is the START tile center (caller's current tile).
 */
export function findPathToBase(startX: number, startY: number): { x: number; y: number }[] | null {
  const start = pixelToGrid(startX, startY);
  const rows = MAP_GRID.length;
  const cols = MAP_GRID[0]?.length ?? 0;

  // Clamp start to grid bounds
  start.col = Math.max(0, Math.min(cols - 1, start.col));
  start.row = Math.max(0, Math.min(rows - 1, start.row));

  // Precompute base tile grid positions
  const baseTargets = BASE_POSITIONS.map(bp => pixelToGrid(bp.x, bp.y));
  const baseKeySet = new Set(baseTargets.map(b => nodeKey(b.col, b.row)));

  const startKey = nodeKey(start.col, start.row);

  // Check if already at base
  if (baseKeySet.has(startKey)) {
    return [gridToPixel(start.col, start.row)];
  }

  // Open set as simple sorted array (fast enough for small grids ≤600 tiles)
  const open: AStarNode[] = [];
  const gMap = new Map<string, number>(); // best g score per node
  const parentMap = new Map<string, string | null>(); // for path reconstruction

  const h0 = heuristic(start.col, start.row, baseTargets);
  open.push({ col: start.col, row: start.row, g: 0, f: h0, parentKey: null });
  gMap.set(startKey, 0);
  parentMap.set(startKey, null);

  while (open.length > 0) {
    // Pop node with smallest f
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i].f < open[bestIdx].f) bestIdx = i;
    }
    const current = open[bestIdx];
    open.splice(bestIdx, 1);

    const curKey = nodeKey(current.col, current.row);

    // Skip if we already found a better path to this node
    if (current.g > (gMap.get(curKey) ?? Infinity)) continue;

    // Goal reached?
    if (baseKeySet.has(curKey)) {
      return reconstructPath(curKey, parentMap);
    }

    // Expand neighbors
    for (const dir of DIRS) {
      const nc = current.col + dir.dc;
      const nr = current.row + dir.dr;

      // Bounds check
      if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;

      const tile = MAP_GRID[nr][nc];
      const cost = TILE_COST[tile];
      if (cost === undefined) continue; // impassable (WALL, WATER)

      // Blocking or kill-zone decorations are impassable
      const decor = getDecorAt(nc, nr);
      if (decor) {
        const di = DECOR_INTERACTIONS[decor.type].interaction;
        if (di === DecorInteraction.BLOCK || di === DecorInteraction.KILL) continue;
      }

      const tentativeG = current.g + cost;
      const nKey = nodeKey(nc, nr);
      const prevG = gMap.get(nKey) ?? Infinity;

      if (tentativeG < prevG) {
        gMap.set(nKey, tentativeG);
        parentMap.set(nKey, curKey);
        const h = heuristic(nc, nr, baseTargets);
        open.push({ col: nc, row: nr, g: tentativeG, f: tentativeG + h, parentKey: curKey });
      }
    }
  }

  // No path found
  return null;
}

/** Reconstruct path from goal back to start, return as pixel waypoints */
function reconstructPath(
  goalKey: string,
  parentMap: Map<string, string | null>,
): { x: number; y: number }[] {
  const keys: string[] = [];
  let k: string | null | undefined = goalKey;
  while (k != null) {
    keys.push(k);
    k = parentMap.get(k) ?? null;
  }
  keys.reverse();

  // Convert grid keys to pixel centers
  return keys.map(key => {
    const [c, r] = key.split(',').map(Number);
    return gridToPixel(c, r);
  });
}

/**
 * Find path from a pixel position to an arbitrary target pixel position.
 * Returns array of pixel waypoints (tile centers), or null if no path found.
 * Used when enemies need to pathfind around walls to reach an aggro target.
 */
export function findPathToPoint(startX: number, startY: number, targetX: number, targetY: number): { x: number; y: number }[] | null {
  const start = pixelToGrid(startX, startY);
  const goal = pixelToGrid(targetX, targetY);
  const rows = MAP_GRID.length;
  const cols = MAP_GRID[0]?.length ?? 0;

  // Clamp to grid bounds
  start.col = Math.max(0, Math.min(cols - 1, start.col));
  start.row = Math.max(0, Math.min(rows - 1, start.row));
  goal.col = Math.max(0, Math.min(cols - 1, goal.col));
  goal.row = Math.max(0, Math.min(rows - 1, goal.row));

  const startKey = nodeKey(start.col, start.row);
  const goalKey = nodeKey(goal.col, goal.row);

  if (startKey === goalKey) {
    return [gridToPixel(start.col, start.row)];
  }

  const open: AStarNode[] = [];
  const gMap = new Map<string, number>();
  const parentMap = new Map<string, string | null>();

  const h0 = Math.abs(start.col - goal.col) + Math.abs(start.row - goal.row);
  open.push({ col: start.col, row: start.row, g: 0, f: h0, parentKey: null });
  gMap.set(startKey, 0);
  parentMap.set(startKey, null);

  while (open.length > 0) {
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i].f < open[bestIdx].f) bestIdx = i;
    }
    const current = open[bestIdx];
    open.splice(bestIdx, 1);

    const curKey = nodeKey(current.col, current.row);
    if (current.g > (gMap.get(curKey) ?? Infinity)) continue;

    if (curKey === goalKey) {
      return reconstructPath(curKey, parentMap);
    }

    for (const dir of DIRS) {
      const nc = current.col + dir.dc;
      const nr = current.row + dir.dr;
      if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;

      const tile = MAP_GRID[nr][nc];
      const cost = TILE_COST[tile];
      if (cost === undefined) continue;

      const decor = getDecorAt(nc, nr);
      if (decor) {
        const di = DECOR_INTERACTIONS[decor.type].interaction;
        if (di === DecorInteraction.BLOCK || di === DecorInteraction.KILL) continue;
      }

      const tentativeG = current.g + cost;
      const nKey = nodeKey(nc, nr);
      const prevG = gMap.get(nKey) ?? Infinity;

      if (tentativeG < prevG) {
        gMap.set(nKey, tentativeG);
        parentMap.set(nKey, curKey);
        const h = Math.abs(nc - goal.col) + Math.abs(nr - goal.row);
        open.push({ col: nc, row: nr, g: tentativeG, f: tentativeG + h, parentKey: curKey });
      }
    }
  }

  return null;
}

/**
 * Smooth an A* path by removing redundant collinear waypoints.
 * Keeps only the turning points (direction changes).
 */
export function smoothPath(path: { x: number; y: number }[]): { x: number; y: number }[] {
  if (path.length <= 2) return path;

  const result: { x: number; y: number }[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const next = path[i + 1];
    // Keep if direction changes
    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    if (dx1 !== dx2 || dy1 !== dy2) {
      result.push(curr);
    }
  }
  result.push(path[path.length - 1]);
  return result;
}
